import * as Yup from 'yup';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Header,
  Icon,
  Segment,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Message,
  Dimmer,
  Loader,
  Confirm,
  Dropdown,
  Grid,
  TextArea,
  Popup,
  Label
} from 'semantic-ui-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import Select from 'react-select';
import { countryOptions, createCountryOptions } from '../../app/common/options/countryOptions';
import { LocationPicker } from '../../components/map/LocationPicker';
import { useStore } from '../../app/stores/store';
import { Company } from '../../app/models/Company';
import { Formik } from 'formik';
import { CompanySportVenue } from '../../app/models/CompanySportVenue';
import WorkingHours, { WorkingHoursData } from './WorkingHours';
import { getDetailedWorkingHours, getWorkingHoursSummary, isCurrentlyOpen } from './workingHoursHelper';

// Helper function to ensure URL has proper protocol
const ensureHttpsProtocol = (url: string | undefined) => {
  if (!url) return url;
  
  // If URL already has protocol, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Add https:// protocol if missing
  return `https://${url}`;
};

// Define the props interface for VenueForm
interface VenueFormProps {
  venue: CompanySportVenue;
  companies: Company[];
  onClose: () => void;
  onSubmit: (venue: CompanySportVenue) => void;
}

const VenueForm = observer(({ venue, companies, onClose, onSubmit }: VenueFormProps) => {
  const { t } = useTranslation();

  const validationSchema = Yup.object({
    name: Yup.string().required(t('venues.validation.venueNameRequired')),
    city: Yup.string().required(t('venues.validation.cityRequired')),
    postalCode: Yup.string().required(t('venues.validation.postalCodeRequired')),
    address: Yup.string().required(t('venues.validation.addressRequired')),
    mobilePhone: Yup.string().required(t('venues.validation.mobilePhoneRequired')),
    email: Yup.string().email(t('venues.validation.invalidEmailFormat')).required(t('venues.validation.emailRequired')),
    description: Yup.string().required(t('validation.descriptionRequired')),
  });

  // Memoize company options to prevent unnecessary re-renders
  const companySelectOptions = useMemo(() =>
    companies.map(company => ({
      key: company.id,
      text: company.companyName,
      value: company.id
    })), [companies]
  );

  // Localized country options
  const localizedCountryOptions = useMemo(() =>
    createCountryOptions(t, countryOptions), [t]
  );

  return (
    <Formik
      initialValues={venue}
      validationSchema={validationSchema}
      onSubmit={(values) => onSubmit(values)}
    >
      {({ handleSubmit, handleChange, handleBlur, values, errors, touched, setFieldValue, isValid, isSubmitting, dirty }) => {

        const workingHoursData = values.sportVenueWorkingHours?.map(wh => ({
          id: wh.id,
          dayOfWeek: wh.dayOfWeek,
          openTime: wh.openTime,
          closeTime: wh.closeTime,
          breakStart: wh.breakStart,
          breakEnd: wh.breakEnd,
          isClosed: wh.isClosed
        })) || [];

        // Memoize the onChange handler to prevent unnecessary re-renders
        const handleWorkingHoursChange = useCallback((workingHours: WorkingHoursData[]) => {
          const sportVenueWorkingHours = workingHours.map(wh => ({
            id: wh.id,
            companySportVenueID: values.id || '00000000-0000-0000-0000-000000000000',
            dayOfWeek: wh.dayOfWeek,
            openTime: wh.openTime,
            closeTime: wh.closeTime,
            breakStart: wh.breakStart,
            breakEnd: wh.breakEnd,
            isClosed: wh.isClosed
          }));
          setFieldValue('sportVenueWorkingHours', sportVenueWorkingHours);
        }, [values.id, setFieldValue]);

        return (
          <Form onSubmit={handleSubmit} error>
            <Form.Field>
              <label>{t('venues.form.venueName')}</label>
              <Input
                name="name"
                placeholder={t('venues.form.venueName')}
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name && errors.name ? true : false}
              />
              {touched.name && errors.name && (
                <Message error content={errors.name} />
              )}
            </Form.Field>

            <Form.Field>
              <label>{t('venues.form.company')}</label>
              <Dropdown
                placeholder={t('venues.form.selectCompany')}
                fluid
                selection
                options={companySelectOptions}
                value={values.companyID || ''}
                onChange={(_, data) => setFieldValue('companyID', data.value)}
              />
            </Form.Field>

            <Form.Group widths="equal" stackable>
              <Form.Field>
                <label>{t('venues.form.city')}</label>
                <Input
                  name="city"
                  placeholder={t('venues.form.city')}
                  value={values.city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.city && errors.city ? true : false}
                />
                {touched.city && errors.city && (
                  <Message error content={errors.city} />
                )}
              </Form.Field>

              <Form.Field>
                <label>{t('venues.form.postalCode')}</label>
                <Input
                  name="postalCode"
                  placeholder={t('venues.form.postalCode')}
                  value={values.postalCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.postalCode && errors.postalCode ? true : false}
                />
                {touched.postalCode && errors.postalCode && (
                  <Message error content={errors.postalCode} />
                )}
              </Form.Field>
            </Form.Group>

            <Form.Field>
              <label>{t('venues.form.address')}</label>
              <Input
                name="address"
                placeholder={t('venues.form.address')}
                value={values.address}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.address && errors.address ? true : false}
              />
              {touched.address && errors.address && (
                <Message error content={errors.address} />
              )}
            </Form.Field>

            <Form.Group widths="equal" stackable>
              <Form.Field>
                <label>{t('venues.form.mobilePhone')}</label>
                <Input
                  name="mobilePhone"
                  placeholder={t('venues.form.mobilePhone')}
                  value={values.mobilePhone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.mobilePhone && errors.mobilePhone ? true : false}
                />
                {touched.mobilePhone && errors.mobilePhone && (
                  <Message error content={errors.mobilePhone} />
                )}
              </Form.Field>

              <Form.Field>
                <label>{t('venues.form.email')}</label>
                <Input
                  name="email"
                  placeholder={t('venues.form.email')}
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && errors.email ? true : false}
                />
                {touched.email && errors.email && (
                  <Message error content={errors.email} />
                )}
              </Form.Field>
            </Form.Group>

            <Form.Field>
              <label>{t('venues.form.description')}</label>
              <TextArea
                name="description"
                placeholder={t('venues.form.description')}
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.description && errors.description ? true : false}
              />
              {touched.description && errors.description && (
                <Message error content={errors.description} />
              )}
            </Form.Field>

            <Form.Field>
              <label>{t('venues.form.youTubeLink')}</label>
              <Input
                name="youTubeLink"
                placeholder={t('venues.form.youTubeLink')}
                value={values.youTubeLink || ''}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Form.Field>

            <Form.Field>
              <label>{t('venues.form.website')}</label>
              <Input
                name="website"
                placeholder={t('venues.form.website')}
                value={values.website || ''}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Form.Field>

            <Form.Field>
              <label>{t('venues.form.country')}</label>
              <Select
                options={localizedCountryOptions}
                value={localizedCountryOptions.find(option => option.value === values.countryID) || null}
                onChange={(selectedOption) => {
                  if (selectedOption) {
                    setFieldValue('countryID', selectedOption.value);
                  }
                }}
                placeholder={t('venues.form.selectCountry')}
                isSearchable
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 1000 })
                }}
                menuPortalTarget={document.body}
              />
            </Form.Field>

            <Header as="h4" dividing>{t('venues.form.location')}</Header>
            <LocationPicker
              onLocationSelect={(lat, lng) => {
                setFieldValue('latitude', lat);
                setFieldValue('longitude', lng);
              }}
              initialLat={values.latitude}
              initialLng={values.longitude}
            />

            <WorkingHours
              title={t('venues.form.venueWorkingHours')}
              workingHours={workingHoursData}
              onChange={handleWorkingHoursChange}
              parentId={values.id || '00000000-0000-0000-0000-000000000000'}
            />

            <Button.Group fluid stackable style={{ marginTop: '20px' }}>
              <Button type="button" onClick={onClose}>{t('common.cancel')}</Button>
              <Button.Or />
              <Button
                positive
                type="submit"
                disabled={!isValid || isSubmitting || !dirty}
                loading={isSubmitting}
              >
                {t('common.save')}
              </Button>
            </Button.Group>
          </Form>
        );
      }}
    </Formik>
  );
});

export default observer(function CompanyVenues() {
  const { t } = useTranslation();
  const { companyStore, companyVenueStore, userStore } = useStore();
  const { loadAllVenues, venueRegistry, loading, loadingInitial } = companyVenueStore;
  const { companyRegistry, loadUserCompanies } = companyStore;
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<CompanySportVenue | null>(null);
  const [isNewVenue, setIsNewVenue] = useState(true);
  const [filterByCompany, setFilterByCompany] = useState<string | null>(null);

  useEffect(() => {
    // Load all venues and companies owned by the user
    if (userStore.user) {
      loadAllVenues();
      loadUserCompanies(userStore.user.userID);
    }
  }, [loadAllVenues, loadUserCompanies, userStore.user]);

  const handleCreateVenue = () => {
    setIsNewVenue(true);
    setSelectedVenue({
      id: '',
      name: '',
      city: '',
      postalCode: '',
      address: '',
      mobilePhone: '',
      email: '',
      description: '',
      latitude: 0,
      longitude: 0,
      sportVenueWorkingHours: []
    });
    setModalOpen(true);
  };

  const handleEditVenue = (venue: CompanySportVenue) => {
    setIsNewVenue(false);
    setSelectedVenue(venue);
    setModalOpen(true);
  };

  const handleDeleteClick = (venue: CompanySportVenue) => {
    setSelectedVenue(venue);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedVenue) {
      await companyVenueStore.deleteVenue(selectedVenue.id);
      setDeleteConfirmOpen(false);
    }
  };

  const handleSubmit = async (venue: CompanySportVenue) => {
    try {
      if (isNewVenue) {
        // Add user ID for tracking who created this venue
        if (userStore.user) {
          venue.createdByUserID = userStore.user.userID;
        }
        await companyVenueStore.createVenue(venue);
        await loadAllVenues(); // Reload to ensure we have the latest data
      } else {
        await companyVenueStore.updateVenue(venue);
      }

      setModalOpen(false);
    } catch (error) {
      console.error("Error submitting venue:", error);
    }
  };

  const venues = Array.from(venueRegistry.values());
  const filteredVenues = filterByCompany
    ? venues.filter(venue => venue.companyID === filterByCompany)
    : venues;

  // Memoize company options to prevent unnecessary re-renders
  const companySelectOptions = useMemo(() => [
    { value: null, label: t('venues.filter.allCompanies') },
    ...Array.from(companyRegistry.values()).map(company => ({
      value: company.id,
      label: company.companyName
    }))
  ], [companyRegistry, t]);

  const getCompanyName = useCallback((companyId?: string) => {
    if (!companyId) return t('venues.table.notAssigned');
    const company = companyRegistry.get(companyId);
    return company ? company.companyName : t('venues.table.unknownCompany');
  }, [companyRegistry, t]);

  if (loadingInitial) {
    return (
      <Segment>
        <Dimmer active inverted>
          <Loader inverted content={t('venues.loading')} />
        </Dimmer>
      </Segment>
    );
  }

  return (
    <Container fluid>
      <style>{`
        @media only screen and (max-width: 767px) {
          .ui.container.fluid {
            margin-left: 0 !important;
            margin-right: 0 !important;
            padding-left: 0.5em !important;
            padding-right: 0.5em !important;
          }
          
          .ui.segment.clearing {
            padding: 0.5em !important;
          }
          
          .ui.table.mobile-card {
            border: none !important;
          }
          
          .ui.table.mobile-card thead {
            display: none;
          }
          
          .ui.table.mobile-card tbody {
            display: block;
          }
          
          .ui.table.mobile-card tr {
            display: block;
            margin-bottom: 1em;
            border: 1px solid rgba(34, 36, 38, 0.15);
            border-radius: 0.28571429rem;
            box-shadow: 0 1px 2px 0 rgba(34, 36, 38, 0.15);
          }
          
          .ui.table.mobile-card td {
            display: flex;
            flex-direction: column;
            padding: 0.5em !important;
            border: none !important;
            text-align: left !important;
          }
          
          .ui.table.mobile-card td:before {
            content: attr(data-label);
            font-weight: bold;
            margin-bottom: 0.3em;
            color: rgba(0, 0, 0, 0.87);
            font-size: 0.9em;
          }
          
          .ui.table.mobile-card td:empty {
            display: none;
          }
          
          .mobile-actions {
            display: flex;
            justify-content: space-around;
            margin-top: 0.5em;
          }
          
          .ui.header h2 {
            font-size: 1.3em !important;
          }
          
          .ui.modal {
            width: 95% !important;
            margin: 1em auto !important;
          }
          
          .ui.modal > .content {
            padding: 1em !important;
          }
          
          /* Working Hours Table Mobile Styles */
          .ui.table.celled.compact thead th {
            font-size: 0.85em !important;
            padding: 0.4em 0.3em !important;
          }
          
          .ui.table.celled.compact thead th button {
            display: block !important;
            margin: 0.2em auto 0 auto !important;
            padding: 0.3em 0.4em !important;
          }
          
          .ui.table.celled.compact tbody td {
            padding: 0.4em 0.3em !important;
          }
          
          .ui.table.celled.compact tbody td .ui.input {
            font-size: 0.9em !important;
          }
          
          .ui.table.celled.compact tbody td .ui.input input {
            padding: 0.5em !important;
          }
        }
      `}</style>
      
      <Header as="h2">
        <Icon name="map marker alternate" />
        <Header.Content>{t('venues.title')}</Header.Content>
      </Header>

      <Segment clearing>
        <Grid stackable>
          <Grid.Row>
            <Grid.Column mobile={16} tablet={8} computer={6}>
              <Select
                options={companySelectOptions}
                value={companySelectOptions.find(option => option.value === filterByCompany) || companySelectOptions[0]}
                onChange={(selectedOption) => {
                  setFilterByCompany(selectedOption ? selectedOption.value : null);
                }}
                placeholder={t('venues.filter.filterByCompany')}
                isClearable
              />
            </Grid.Column>
            <Grid.Column mobile={16} tablet={8} computer={6}>
              {filteredVenues.length === 0 && (
                <Message info>
                  <Message.Header>{t('venues.noVenuesFound.header')}</Message.Header>
                  <p>{t('venues.noVenuesFound.message')}</p>
                </Message>
              )}
            </Grid.Column>
            <Grid.Column mobile={16} tablet={16} computer={4} textAlign="right">
              <Button
                primary
                icon
                labelPosition="left"
                onClick={handleCreateVenue}
                fluid
              >
                <Icon name="add" />
                {t('venues.addVenue')}
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        {filteredVenues.length > 0 && (
          <Table celled striped className="mobile-card">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>{t('venues.table.venueName')}</Table.HeaderCell>
                <Table.HeaderCell>{t('venues.table.company')}</Table.HeaderCell>
                <Table.HeaderCell>{t('venues.table.location')}</Table.HeaderCell>
                <Table.HeaderCell>{t('venues.table.contact')}</Table.HeaderCell>
                <Table.HeaderCell>{t('venues.table.workingHours')}</Table.HeaderCell>
                <Table.HeaderCell>{t('venues.table.description')}</Table.HeaderCell>
                <Table.HeaderCell width={2}>{t('venues.table.actions')}</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {filteredVenues.map((venue) => (
                <Table.Row key={venue.id}>
                  <Table.Cell data-label={t('venues.table.venueName')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span>{venue.name}</span>
                      {isCurrentlyOpen(venue.sportVenueWorkingHours) && (
                        <Label color="green" size="mini">
                          <Icon name="circle" />
                          {t('venues.table.openNow')}
                        </Label>
                      )}
                    </div>
                  </Table.Cell>
                  <Table.Cell data-label={t('venues.table.company')}> 
                    <p>
                    {getCompanyName(venue.companyID)}
                    </p>
                    </Table.Cell>
                  <Table.Cell data-label={t('venues.table.location')}>
                    <p>
                    {venue.city}, {venue.address}, {venue.postalCode}
                    </p>
                  </Table.Cell>
                  <Table.Cell data-label={t('venues.table.contact')}>
                    <div>
                      <Icon name="phone" /> {venue.mobilePhone}
                    </div>
                    <div style={{ marginTop: '0.3em' }}>
                      <Icon name="mail" /> {venue.email}
                    </div>
                  </Table.Cell>
                  <Table.Cell data-label={t('venues.table.workingHours')}>
                    <Popup
                      trigger={
                        <div style={{ cursor: 'pointer' }}>
                          <Icon name="clock outline" />
                          {getWorkingHoursSummary(venue.sportVenueWorkingHours)}
                        </div>
                      }
                      content={
                        <div style={{ whiteSpace: 'pre-line', fontSize: '12px' }}>
                          <strong>{t('venues.table.workingHours')}:</strong><br />
                          {getDetailedWorkingHours(venue.sportVenueWorkingHours)}
                        </div>
                      }
                      position="top center"
                      size="small"
                      inverted
                    />
                  </Table.Cell>
                  <Table.Cell data-label={t('venues.table.description')}>
                    <p>
                    {venue.description && venue.description.length > 30
                      ? `${venue.description.substring(0, 30)}...`
                      : venue.description}
                    </p>
                  </Table.Cell>
                  <Table.Cell data-label={t('venues.table.actions')}>
                    <p>
                    <Button.Group size="mini" className="mobile-actions">
                      <Button
                        icon="edit"
                        color="blue"
                        onClick={() => handleEditVenue(venue)}
                      />
                      <Button
                        icon="linkify"
                        color="green"
                        disabled={!venue.youTubeLink}
                        onClick={() => window.open(ensureHttpsProtocol(venue.youTubeLink), '_blank')}
                      />
                      <Button
                        icon="trash"
                        color="red"
                        onClick={() => handleDeleteClick(venue)}
                      />
                    </Button.Group>
                    </p>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Segment>

      {/* Create/Edit Venue Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        size="large"
      >
        <Modal.Header>
          {isNewVenue ? t('venues.modal.createNewVenue') : t('venues.modal.editVenue')}
        </Modal.Header>
        <Modal.Content scrolling>
          {selectedVenue && (
            <VenueForm
              venue={selectedVenue}
              companies={Array.from(companyRegistry.values())}
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
          )}
        </Modal.Content>
      </Modal>

      {/* Delete Confirmation */}
      <Confirm
        open={deleteConfirmOpen}
        header={t('venues.delete.header', { venueName: selectedVenue?.name })}
        content={t('venues.delete.content')}
        confirmButton={t('venues.delete.confirmButton')}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
    </Container>
  );
});